// @flow
import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { AutoSizer, Grid, WindowScroller, CellMeasurer, CellMeasurerCache } from 'react-virtualized'
import { ICMSBanner } from '~/componentsV2/cmsBanners/types'
import { BannerInjectComponent } from '~/componentsV2/cmsBanners/banner'
import ProductItem from '~/components/product-item'
import AntiqueRugsProductItem from '~/components/product-item-antique-rugs'
import { TGridVariant } from './types'
import {
  CollectionType,
  PaginationType,
  ProductItemComponentType,
  ProductItemType,
} from '~/@types/models'
import { useMediaQuery } from 'react-responsive'
import { TProductWithGrid } from '~/hooks/useCollectionData'
import ResizeObserver from 'rc-resize-observer'
import { Box } from 'theme-ui'
import CustomContextMenu from '~/componentsV2/contextMenu'
import { addWatermarkAndDownload } from '~/componentsV2/contextMenu/ImageWatermark'

type TCurrentData = readonly (TProductWithGrid | ProductItemType | ICMSBanner)[]

interface GridProps {
  data: CollectionType & {
    pagination: PaginationType
  }
  gridVariant: TGridVariant
  currentData: TCurrentData
  initialItemsCount?: number
  template: 'default' | 'bespoke'
  noLink?: boolean
}

const cache = new CellMeasurerCache({
  minHeight: 100,
  fixedWidth: true,
})

const CollectionGridVirtualized = ({
  data,
  currentData,
  gridVariant = 'layout.template-default',
  template,
  noLink = false,
}: GridProps) => {
  const [scrollToColumn, setScrollToColumn] = useState<number | undefined>(undefined)
  const [scrollToRow, setScrollToRow] = useState<number | undefined>(undefined)

  const parentRef = React.useRef<HTMLDivElement>(null)
  const gridRef = React.useRef<Grid>(null)
  const contextMenuRef = React.useRef<HTMLDivElement>(null)
  const [clickedItem, setClickedItem] = useState<ProductItemType | null>(null)

  useEffect(() => {
    if (cache?.clearAll && gridRef?.current) {
      cache.clearAll()
      gridRef.current?.recomputeGridSize()
    }
  }, [currentData])

  const isMobile = useMediaQuery({
    query: `(max-width: 896px)`,
  })

  const COLLECTION_CONFIGS = useMemo(
    () => ({
      'layout.template-default': { cols: isMobile ? 2 : 3 },
      'layout.template-furniture': { cols: isMobile ? 2 : 3 },
      'layout.template-rugs': { cols: isMobile ? 2 : 2 },
      'layout.template-antique-rugs': { cols: isMobile ? 2 : 3 },
    }),
    [isMobile]
  )

  const activeConfig = useMemo(() => {
    return (
      COLLECTION_CONFIGS[gridVariant as keyof typeof COLLECTION_CONFIGS] ||
      COLLECTION_CONFIGS['layout.template-default']
    )
  }, [gridVariant, COLLECTION_CONFIGS])

  const columnWidth = useMemo(() => {
    const width = parentRef.current?.clientWidth || 0
    return Math.floor(width / activeConfig.cols)
  }, [parentRef.current?.clientWidth, activeConfig])

  const rowCount = useMemo(() => {
    let additionalRows = 0
    currentData.forEach(item => {
      if ('__banner' in item && isMobile) {
        additionalRows += 1
      }
    })

    const baseRowCount = Math.ceil(currentData.length / activeConfig.cols)
    return baseRowCount + additionalRows
  }, [currentData, activeConfig.cols])

  const transformedData = useMemo(() => {
    const processedData: (TProductWithGrid | ProductItemType | ICMSBanner | null)[] = []

    currentData.forEach(item => {
      if ('__banner' in item && isMobile) {
        processedData.push(item)
        processedData.push(null)
      } else {
        processedData.push(item)
      }
    })
    return processedData
  }, [currentData])

  const renderContent = useCallback(
    (item, gridVariant) => {
      if ('__banner' in item) {
        const banner = item as ICMSBanner
        return (
          <BannerInjectComponent
            banner={banner}
            isRugs={gridVariant === 'layout.template-rugs'}
            gridLayout={gridVariant}
          />
        )
      }

      return gridVariant !== 'layout.template-antique-rugs' && template === 'default' ? (
        <ProductItem
          gridLayout={item.gridLayout}
          product={
            {
              ...item,
            } as ProductItemComponentType
          }
          onItemClick={() => setClickedItem(item)}
          tag={item.metafields.cardCollectionTag}
          productImageRatio={1}
          showPrice={data.metafields?.showPrize}
          visibleVariantsCount={6}
          imageSize={item.productType.toLowerCase().includes('rug') ? 'FULLSCREEN' : 'OVERHEAD'}
          titleNotePosition={1}
          productVariant={item.productVariant}
          collectionHandle={data.handle}
          isNoScaleCustom={data.handle === 'circular'}
          onlyCompareAtPriceVariants={data.metafields?.onlyCompareAtPriceVariants}
          hiddenInfo={false}
          template={template}
        />
      ) : (
        <AntiqueRugsProductItem
          onItemClick={() => setClickedItem(item)}
          template={template}
          noLink={noLink}
          product={
            {
              ...item,
            } as ProductItemComponentType
          }
        />
      )
    },
    [currentData, gridVariant, data]
  )

  const cellRenderer = useCallback(
    ({ columnIndex, key, rowIndex, style, parent }) => {
      const index = rowIndex * activeConfig.cols + columnIndex
      const item = transformedData[index]

      if (!item) return null

      const isBanner = '__banner' in item
      const colWidth = Math.floor(100 / activeConfig.cols) + '%'

      return (
        <CellMeasurer
          key={`${key}-${item.id}`}
          cache={cache}
          columnIndex={columnIndex}
          parent={parent}
          rowIndex={rowIndex}
        >
          {({ registerChild }) => (
            <ResizeObserver
              key={key}
              onResize={(size, element) => {
                const _height = cache.getHeight(rowIndex, columnIndex)
                const _width = cache.getWidth(rowIndex, columnIndex)

                if ((_height !== size.height || _width !== size.width) && gridRef.current) {
                  cache.set(rowIndex, columnIndex, size.width, size.height)

                  gridRef.current?.recomputeGridSize({
                    columnIndex,
                    rowIndex,
                  })
                }
              }}
            >
              <Box
                ref={registerChild}
                sx={{
                  ...style,
                  width: isBanner ? ['100%', null, null, colWidth] : colWidth,
                  gridColumn: isBanner ? `1 / ${activeConfig.cols + 1}` : 'auto',
                  height: 'auto',
                  p: '10px',
                }}
              >
                {renderContent(item, gridVariant)}
              </Box>
            </ResizeObserver>
          )}
        </CellMeasurer>
      )
    },
    [currentData, renderContent, activeConfig, gridVariant]
  )

  return (
    <CustomContextMenu
      ref={contextMenuRef}
      actions={[
        {
          label: 'Go to Product',
          group: 'Product',
          onClick: () => {
            window.open(`/products/${clickedItem?.handle}`)
          },
        },
        {
          label: 'Save Image',
          group: 'Image',
          onClick: () => {
            const watermarkText = window.location.origin
            const logoUrl = '/media/2024/logo/ben-logo.jpeg'
            addWatermarkAndDownload(
              clickedItem?.mainImage.srcDesktop || '',
              watermarkText,
              logoUrl,
              clickedItem?.title
            )
          },
        },
      ]}
    >
      <div ref={parentRef} style={{ width: '100%' }}>
        <WindowScroller>
          {({ height, isScrolling, registerChild, onChildScroll, scrollTop }) => (
            <AutoSizer disableHeight>
              {({ width }) => (
                <div ref={registerChild}>
                  <Grid
                    cellRenderer={cellRenderer}
                    columnWidth={columnWidth}
                    columnCount={activeConfig.cols}
                    height={height}
                    overscanColumnCount={1}
                    overscanRowCount={5}
                    deferredMeasurementCache={cache}
                    rowHeight={cache.rowHeight}
                    rowCount={rowCount}
                    scrollToColumn={scrollToColumn}
                    scrollToRow={scrollToRow}
                    autoHeight
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    scrollTop={scrollTop}
                    width={width}
                    ref={gridRef}
                  />
                </div>
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      </div>
    </CustomContextMenu>
  )
}

export default CollectionGridVirtualized
